import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { entityClient } from '../services/entityClient';
import { Plus, Trash2, X, FileText, Download, PlusCircle, Send, AlertTriangle, CheckCircle, Clock, Building2, RotateCcw, Activity } from 'lucide-react';
import InvoiceLifecycleWizard from '../components/InvoiceLifecycleWizard';
import InvoiceVersionsPanel from '../components/InvoiceVersionsPanel';
import { ActionGatedButton } from '../components/ActionGate';
import { useFocusEntity } from '../hooks/useFocusEntity';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import QRCode from 'qrcode';
import { convertArabic } from 'arabic-reshaper';
import { NotoSansArabicBase64 } from '../fonts/NotoSansArabic';

// Helper function to process Arabic text for proper display in PDF
// Arabic text needs to be reshaped (connected letters) for correct display
// Note: With proper Arabic fonts like NotoSansArabic, NO reversal is needed
const processArabicText = (text) => {
  if (!text) return '';
  const arabicRegex = /[\u0600-\u06FF]/;
  if (!arabicRegex.test(text)) return text;
  
  try {
    // Reshape Arabic letters to connect them properly
    // The font handles RTL direction automatically
    return convertArabic(text);
  } catch (e) {
    console.error('Arabic text processing error:', e);
    return text;
  }
};

const Invoices = ({ user }) => {
  const { focusedId, registerRow } = useFocusEntity();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  // ── Credit Note state (Phase Accounting-CN-1) ────────────────
  const [showCNModal, setShowCNModal] = useState(false);
  const [cnInvoice, setCnInvoice] = useState(null);
  const [cnExisting, setCnExisting] = useState([]);
  const [cnForm, setCnForm] = useState({ amount: '', type: 'partial_refund', reason: '', notes: '' });
  const [cnBusy, setCnBusy] = useState(false);
  // Phase 5 — Lifecycle wizard
  const [lifecycleInvoiceId, setLifecycleInvoiceId] = useState(null);

  const openCreditNoteModal = async (invoice) => {
    setCnInvoice(invoice);
    setCnForm({ amount: '', type: 'partial_refund', reason: '', notes: '' });
    setShowCNModal(true);
    try {
      const r = await axiosInstance.get(`/credit-notes/by-invoice/${invoice.id}`);
      setCnExisting(r.data || []);
    } catch (e) {
      setCnExisting([]);
    }
  };

  const submitCreditNote = async (postNow = false) => {
    if (!cnInvoice) return;
    const amount = parseFloat(cnForm.amount);
    if (!amount || amount <= 0) { toast.error('Amount must be > 0'); return; }
    if (!cnForm.reason || cnForm.reason.trim().length < 3) { toast.error('Reason must be at least 3 characters'); return; }
    setCnBusy(true);
    try {
      const created = await axiosInstance.post('/credit-notes', {
        invoice_id: cnInvoice.id,
        amount,
        type: cnForm.type,
        reason: cnForm.reason.trim(),
        notes: cnForm.notes || null,
      });
      toast.success(`Credit Note ${created.data.credit_note_number} created`);
      if (postNow) {
        await axiosInstance.post(`/credit-notes/${created.data.id}/post`);
        toast.success(`Posted to accounting (journal created)`);
      }
      // Refresh existing list + invoices to surface new refund_state
      const r = await axiosInstance.get(`/credit-notes/by-invoice/${cnInvoice.id}`);
      setCnExisting(r.data || []);
      const invs = await axiosInstance.get('/invoices');
      setInvoices(invs.data);
      setCnForm({ amount: '', type: 'partial_refund', reason: '', notes: '' });
      if (postNow) setShowCNModal(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Failed');
    } finally {
      setCnBusy(false);
    }
  };

  const postExistingCN = async (cnId) => {
    try {
      await axiosInstance.post(`/credit-notes/${cnId}/post`);
      toast.success('Credit Note posted');
      const r = await axiosInstance.get(`/credit-notes/by-invoice/${cnInvoice.id}`);
      setCnExisting(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const canCreditNote = (invoice) => invoice.status && invoice.status !== 'draft';
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [submittingToZATCA, setSubmittingToZATCA] = useState(false);
  const [zatcaAlerts, setZatcaAlerts] = useState([]);
  
  // Check if user can delete/cancel an invoice
  const canDelete = (invoice) => {
    if (invoice.status === 'cancelled') return false;
    if (user?.role === 'admin') return true;
    return invoice.status === 'draft';
  };
  
  // Get ZATCA status badge
  const getZATCAStatusBadge = (status) => {
    const normalizedStatus = (status || '').toLowerCase();
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'جاهزة للإرسال' },
      submitted: { color: 'bg-blue-100 text-blue-700', icon: Send, label: 'تم الإرسال' },
      cleared: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'معتمدة (B2B)' },
      reported: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'مُبلغ عنها (B2C)' },
      rejected: { color: 'bg-red-100 text-red-700', icon: X, label: 'مرفوضة' },
      error: { color: 'bg-red-100 text-red-700', icon: AlertTriangle, label: 'خطأ' }
    };
    
    const config = statusConfig[normalizedStatus] || { color: 'bg-gray-100 text-gray-600', icon: Clock, label: 'غير مُرسلة' };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };
  
  // Submit invoice to ZATCA
  const submitToZATCA = async (invoiceId) => {
    try {
      setSubmittingToZATCA(true);
      const response = await axiosInstance.post(`/zatca/submit/${invoiceId}`);
      toast.success(response.data.message || 'تم إرسال الفاتورة إلى ZATCA بنجاح');
      fetchData();
      // Update selected invoice
      if (selectedInvoice?.id === invoiceId) {
        setSelectedInvoice(prev => ({...prev, zatca_status: response.data.status || 'REPORTED'}));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل في إرسال الفاتورة إلى ZATCA');
    } finally {
      setSubmittingToZATCA(false);
    }
  };
  
  // Calculate ZATCA alerts for invoices not sent within specified days
  const calculateZATCAAlerts = (invoicesList) => {
    const alertDays = 3; // تنبيه بعد 3 أيام من إنشاء الفاتورة
    const now = new Date();
    
    const alerts = invoicesList.filter(inv => {
      // Only check invoices that haven't been sent to ZATCA
      const status = (inv.zatca_status || '').toLowerCase();
      if (status === 'reported' || status === 'cleared') return false;
      
      // Check if invoice is older than alert days
      const createdAt = new Date(inv.created_at);
      const daysDiff = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24));
      return daysDiff >= alertDays && inv.status !== 'cancelled' && inv.status !== 'draft';
    });
    
    setZatcaAlerts(alerts);
  };
  
  const [formData, setFormData] = useState({
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    customer_id: '',
    project_id: '',          // P-Fix · Reconciliation FAIL: Project Profit ↔ GL needs project tagging
    client_name_arabic: '',
    purchase_order_no: '',
    subject: '',
    items: [{ description: '', quantity: 1, unit: 'unit', unit_price: 0, total: 0, inventory_id: '' }],
    subtotal: 0,
    vat_rate: 15,
    vat_amount: 0,
    total: 0,
    amount_paid: 0,
    amount_remaining: 0,
    payment_terms: '',
    notes: ''
  });
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchData();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, customersRes, inventoryRes, projectsRes] = await Promise.all([
        axiosInstance.get('/invoices'),
        axiosInstance.get('/customers'),
        axiosInstance.get('/inventory'),
        axiosInstance.get('/projects').catch(() => ({ data: [] })),
      ]);
      setInvoices(invoicesRes.data);
      setCustomers(customersRes.data);
      setInventory(inventoryRes.data);
      setProjects(projectsRes.data || []);
      calculateZATCAAlerts(invoicesRes.data);
    } catch (error) {
      if (loading) {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setSelectedCustomer(customer);
    setFormData({
      ...formData,
      customer_id: customerId,
      client_name_arabic: customer?.name_arabic || ''
    });
  };

  const calculateTotals = (items, vatRate, amountPaid = 0) => {
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const vatAmount = subtotal * (vatRate / 100);
    const total = subtotal + vatAmount;
    const amountRemaining = total - amountPaid;
    return { subtotal, vatAmount, total, amountRemaining };
  };

  const handleAmountPaidChange = (value) => {
    const amountPaid = parseFloat(value) || 0;
    const amountRemaining = formData.total - amountPaid;
    setFormData({
      ...formData,
      amount_paid: amountPaid,
      amount_remaining: amountRemaining
    });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    
    if (field === 'inventory_id') {
      const selectedItem = inventory.find(inv => inv.id === value);
      if (selectedItem) {
        newItems[index] = {
          ...newItems[index],
          inventory_id: value,
          description: selectedItem.name || selectedItem.item_name,
          unit: selectedItem.unit || 'unit',
          unit_price: selectedItem.unit_price || 0,
          total: (newItems[index].quantity || 1) * (selectedItem.unit_price || 0)
        };
      } else {
        newItems[index].inventory_id = value;
        newItems[index].description = '';
      }
    } else {
      newItems[index][field] = value;
      
      if (field === 'quantity' || field === 'unit_price') {
        const qty = parseFloat(newItems[index].quantity) || 0;
        const price = parseFloat(newItems[index].unit_price) || 0;
        newItems[index].total = qty * price;
      }
    }
    
    const { subtotal, vatAmount, total, amountRemaining } = calculateTotals(newItems, formData.vat_rate, formData.amount_paid);
    setFormData({
      ...formData,
      items: newItems,
      subtotal,
      vat_amount: vatAmount,
      total,
      amount_remaining: amountRemaining
    });
  };

  const handleVatChange = (vatRate) => {
    const { subtotal, vatAmount, total, amountRemaining } = calculateTotals(formData.items, vatRate, formData.amount_paid);
    setFormData({
      ...formData,
      vat_rate: vatRate,
      vat_amount: vatAmount,
      total,
      amount_remaining: amountRemaining
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit: 'unit', unit_price: 0, total: 0, inventory_id: '' }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length <= 1) return;
    const newItems = formData.items.filter((_, i) => i !== index);
    const { subtotal, vatAmount, total, amountRemaining } = calculateTotals(newItems, formData.vat_rate, formData.amount_paid);
    setFormData({
      ...formData,
      items: newItems,
      subtotal,
      vat_amount: vatAmount,
      total,
      amount_remaining: amountRemaining
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    
    if (formData.items.some(item => !item.description || item.total === 0)) {
      toast.error('Please fill all item details');
      return;
    }

    try {
      const customer = customers.find(c => c.id === formData.customer_id);
      const payload = {
        ...formData,
        client_name: customer?.name_english || customer?.name_arabic || '',
        client_name_arabic: formData.client_name_arabic || customer?.name_arabic || '',
        client_email: customer?.email || '',
        purchase_order_no: formData.purchase_order_no,
        tax_rate: formData.vat_rate,
        tax_amount: formData.vat_amount,
        amount_paid: formData.amount_paid,
        amount_remaining: formData.amount_remaining
      };
      const response = await axiosInstance.post('/invoices', payload);
      if (response.data.approval_status === 'pending') {
        toast.success('Invoice submitted for approval', {
          description: 'Waiting for admin approval before it appears in the list'
        });
      } else {
        toast.success('Invoice created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axiosInstance.put(`/invoices/${id}/status?status=${status}`);
      toast.success(`Invoice status updated to ${status}`);
      fetchData();
    } catch (error) {
      // Surface Action Engine denial reasons (403 from invoice.post gate).
      const detail = error.response?.data?.detail;
      let msg = 'Failed to update status';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (detail && typeof detail === 'object') {
        const reason = detail.reason || detail.action;
        const failed = (detail.failed_preconditions || []).join('، ');
        msg = failed ? `${reason || 'لا يمكن تغيير الحالة'} (${failed})` : (reason || msg);
      }
      toast.error(msg);
    }
  };

  const handleDelete = async (invoice) => {
    const id = typeof invoice === 'string' ? invoice : invoice.id;
    const displayName = typeof invoice === 'object'
      ? (invoice.invoice_number || invoice.id)
      : undefined;
    const res = await entityClient.delete('invoice', id, { displayName });
    if (res.mode === 'deleted') { toast.success('تم إلغاء الفاتورة (قيد عكسي) — المسودات تُحذف نهائياً'); fetchData(); }
    else if (res.mode === 'archived') { fetchData(); }
    else if (res.mode === 'error') { toast.error('فشل الحذف: ' + (res.error?.response?.data?.detail || res.error?.message || '')); }
  };

  // ZATCA TLV QR Code Generator
  const generateZATCAQR = (sellerName, vatNumber, timestamp, totalWithVAT, vatAmount) => {
    const tlvEncode = (tag, value) => {
      const valueBytes = new TextEncoder().encode(value);
      return new Uint8Array([tag, valueBytes.length, ...valueBytes]);
    };
    
    const tlv1 = tlvEncode(1, sellerName);
    const tlv2 = tlvEncode(2, vatNumber);
    const tlv3 = tlvEncode(3, timestamp);
    const tlv4 = tlvEncode(4, totalWithVAT);
    const tlv5 = tlvEncode(5, vatAmount);
    
    const combined = new Uint8Array([...tlv1, ...tlv2, ...tlv3, ...tlv4, ...tlv5]);
    
    // Convert to Base64
    let binary = '';
    combined.forEach(byte => binary += String.fromCharCode(byte));
    return btoa(binary);
  };

  // Get Arabic text as image from backend
  const getArabicImage = async (text, fontSize = 14) => {
    try {
      const response = await axiosInstance.post('/arabic-image', {
        text,
        font_size: fontSize,
        color_r: 10,
        color_g: 66,
        color_b: 117
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get Arabic image:', error);
      return null;
    }
  };

  const downloadPDF = async (invoice) => {
    const doc = new jsPDF();
    
    // Add Arabic font to jsPDF
    doc.addFileToVFS('NotoSansArabic-Regular.ttf', NotoSansArabicBase64);
    doc.addFont('NotoSansArabic-Regular.ttf', 'NotoSansArabic', 'normal');
    
    // Add Company Logo - skip if fails (width increased 1.5x, height same)
    try {
      const logoResponse = await fetch('/logo.png');
      if (logoResponse.ok) {
        const logoBlob = await logoResponse.blob();
        const logoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(logoBlob);
        });
        doc.addImage(logoBase64, 'PNG', 10, 5, 52, 30);
      }
    } catch (err) {
      console.error('Logo load failed:', err);
    }
    
    // Company Header - Arabic Name using Arabic font
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(10, 66, 117);
    doc.text(processArabicText('شركة الطريق العالي المتميز'), 190, 10, { align: 'right' });
    
    // English company name
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Distinct High Road', 190, 16, { align: 'right' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text('Hafsa Bint Omar Street, AlRawdah Dist, Riyadh, KSA', 190, 22, { align: 'right' });
    doc.text('VAT: 314477769800003 | CR: 7052942476', 190, 27, { align: 'right' });
    doc.text('Tel: +966552168158 | Email: Info@dhr-sa.com', 190, 32, { align: 'right' });
    
    // Horizontal line
    doc.setDrawColor(10, 66, 117);
    doc.setLineWidth(0.5);
    doc.line(20, 38, 190, 38);
    
    // Invoice Title
    doc.setFontSize(18);
    doc.setTextColor(10, 66, 117);
    doc.setFont(undefined, 'bold');
    doc.text('INVOICE', 105, 48, { align: 'center' });
    doc.setFont(undefined, 'normal');
    
    // Two columns: Client Details (Left) | Invoice Details (Right)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(10, 66, 117);
    doc.text('Client Details', 20, 58);
    doc.text('Invoice Details', 115, 58);
    
    // Client Details (Left Side)
    doc.setTextColor(0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    let clientY = 66;
    
    // Check if client_name contains Arabic
    const clientName = invoice.client_name || '';
    const hasArabicClientName = /[\u0600-\u06FF]/.test(clientName);
    if (hasArabicClientName) {
      doc.text('Client: ', 20, clientY);
      doc.setFont('NotoSansArabic', 'normal');
      doc.text(processArabicText(clientName), 35, clientY);
      doc.setFont('helvetica', 'normal');
    } else {
      doc.text(`Client: ${clientName}`, 20, clientY);
    }
    clientY += 6;
    
    // Add Arabic client name using Arabic font (if different from client_name)
    if (invoice.client_name_arabic && invoice.client_name_arabic !== clientName) {
      doc.setFont('NotoSansArabic', 'normal');
      doc.text(processArabicText(invoice.client_name_arabic), 20, clientY);
      doc.setFont('helvetica', 'normal');
      clientY += 6;
    }
    
    // Address - use Arabic font if needed
    const addressText = invoice.customer_address || 'N/A';
    const hasArabicAddress = /[\u0600-\u06FF]/.test(addressText);
    if (hasArabicAddress) {
      doc.text('Address: ', 20, clientY);
      doc.setFont('NotoSansArabic', 'normal');
      doc.text(processArabicText(addressText), 38, clientY);
      doc.setFont('helvetica', 'normal');
    } else {
      doc.text(`Address: ${addressText}`, 20, clientY);
    }
    clientY += 6;
    
    doc.text(`CR Number: ${invoice.customer_cr_number || 'N/A'}`, 20, clientY);
    clientY += 6;
    doc.text(`VAT No: ${invoice.customer_vat_number || 'N/A'}`, 20, clientY);
    
    // Invoice Details (Right Side)
    doc.setFontSize(9);
    let invY = 66;
    doc.text(`Invoice No: ${invoice.invoice_number}`, 115, invY);
    invY += 6;
    if (invoice.purchase_order_no) {
      doc.text(`P.O. No: ${invoice.purchase_order_no}`, 115, invY);
      invY += 6;
    }
    doc.text(`Date: ${new Date(invoice.created_at).toLocaleDateString()}`, 115, invY);
    invY += 6;
    doc.text(`Due Date: ${new Date(invoice.due_date).toLocaleDateString()}`, 115, invY);
    invY += 6;
    doc.text(`Status: ${invoice.status.toUpperCase()}`, 115, invY);
    invY += 6;
    // Amount Paid & Remaining under Status
    doc.setTextColor(0, 128, 0);
    doc.text(`Paid: ${(invoice.amount_paid || 0).toFixed(2)} SAR`, 115, invY);
    invY += 6;
    doc.setTextColor(220, 0, 0);
    doc.text(`Remaining: ${(invoice.amount_remaining || invoice.total).toFixed(2)} SAR`, 115, invY);
    doc.setTextColor(0);
    
    // Subject - with Arabic support
    let tableStartY = 108;
    if (invoice.subject) {
      doc.setFontSize(10);
      doc.setTextColor(10, 66, 117);
      doc.text('Subject:', 20, 103);
      doc.setTextColor(0);
      doc.setFontSize(9);
      const subjectText = invoice.subject;
      const hasArabicSubject = /[\u0600-\u06FF]/.test(subjectText);
      if (hasArabicSubject) {
        doc.setFont('NotoSansArabic', 'normal');
        doc.text(processArabicText(subjectText), 40, 103);
        doc.setFont('helvetica', 'normal');
      } else {
        doc.text(subjectText, 40, 103);
      }
      tableStartY = 110;
    }
    
    // Items Table - with Arabic support for descriptions
    // Store which rows have Arabic text for font selection
    const arabicRows = [];
    const tableData = invoice.items.map((item, index) => {
      const desc = item.description || '';
      const hasArabic = /[\u0600-\u06FF]/.test(desc);
      if (hasArabic) {
        arabicRows.push(index);
      }
      return [
        (index + 1).toString(),
        hasArabic ? processArabicText(desc) : desc,
        item.quantity.toString(),
        item.unit || 'unit',
        `${item.unit_price.toFixed(2)} SAR`,
        `${item.total.toFixed(2)} SAR`
      ];
    });
    
    autoTable(doc, {
      startY: tableStartY,
      head: [['#', 'Description', 'Qty', 'Unit', 'Price', 'Total']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [10, 66, 117], font: 'helvetica', fontStyle: 'bold' },
      styles: {
        font: 'helvetica',
        fontSize: 9
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        1: { cellWidth: 70 },
        2: { halign: 'center', cellWidth: 15 },
        3: { halign: 'center', cellWidth: 18 },
        4: { halign: 'right', cellWidth: 30 },
        5: { halign: 'right', cellWidth: 30 }
      },
      didParseCell: function(data) {
        // Apply Arabic font to description column for rows with Arabic text
        if (data.section === 'body' && data.column.index === 1) {
          if (arabicRows.includes(data.row.index)) {
            data.cell.styles.font = 'NotoSansArabic';
          }
        }
        // Also check unit column for Arabic units
        if (data.section === 'body' && data.column.index === 3) {
          const unitText = data.cell.raw;
          if (unitText && /[\u0600-\u06FF]/.test(unitText)) {
            data.cell.styles.font = 'NotoSansArabic';
          }
        }
      }
    });
    
    const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 150;
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.text(`Subtotal: ${invoice.subtotal.toFixed(2)} SAR`, 140, finalY);
    doc.text(`VAT (${invoice.tax_rate || 15}%): ${invoice.tax_amount.toFixed(2)} SAR`, 140, finalY + 6);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`Total: ${invoice.total.toFixed(2)} SAR`, 140, finalY + 14);
    doc.setFont(undefined, 'normal');
    
    // Payment Terms
    let termsY = finalY + 25;
    doc.setFontSize(10);
    doc.setTextColor(10, 66, 117);
    doc.text('Payment Terms:', 20, termsY);
    doc.setTextColor(0);
    doc.text(invoice.payment_terms || 'Due upon receipt', 55, termsY);
    
    // Bank Details (Vertical Layout)
    const bankY = termsY + 12;
    doc.setFontSize(11);
    doc.setTextColor(10, 66, 117);
    doc.text('Bank Details', 20, bankY);
    doc.setTextColor(0);
    doc.setFontSize(9);
    
    const bankDataVertical = [
      ['Bank Name', 'ALAHLI BANK (SNB)'],
      ['Account', '24700000524008'],
      ['IBAN', 'SA6910000024700000524008'],
      ['SWIFT', 'NCBKSAJE']
    ];
    
    autoTable(doc, {
      startY: bankY + 5,
      body: bankDataVertical,
      theme: 'striped',
      columnStyles: {
        0: { cellWidth: 35, fontStyle: 'bold', fillColor: [10, 66, 117], textColor: [255, 255, 255] },
        1: { cellWidth: 60 }
      },
      styles: { fontSize: 9 }
    });
    
    // Notes - with Arabic support
    if (invoice.notes) {
      const notesY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 200;
      doc.setFontSize(10);
      doc.setTextColor(10, 66, 117);
      doc.setFont('helvetica', 'bold');
      doc.text('Notes:', 20, notesY);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0);
      doc.setFontSize(9);
      
      const notesText = invoice.notes;
      const hasArabicNotes = /[\u0600-\u06FF]/.test(notesText);
      if (hasArabicNotes) {
        doc.setFont('NotoSansArabic', 'normal');
        const notesLines = doc.splitTextToSize(processArabicText(notesText), 170);
        doc.text(notesLines, 20, notesY + 6);
        doc.setFont('helvetica', 'normal');
      } else {
        const notesLines = doc.splitTextToSize(notesText, 170);
        doc.text(notesLines, 20, notesY + 6);
      }
    }
    
    // Generate ZATCA Compliant QR Code (TLV Format - Base64)
    // Tag 1: Seller Name (Arabic)
    // Tag 2: VAT Registration Number  
    // Tag 3: Invoice Timestamp (ISO 8601)
    // Tag 4: Invoice Total (with VAT)
    // Tag 5: VAT Amount
    
    const sellerNameArabic = 'شركة الطريق العالي المتميز';
    const vatNumber = '314477769800003';
    const invoiceDate = new Date(invoice.created_at);
    const timestamp = invoiceDate.toISOString();
    const totalWithVAT = invoice.total.toFixed(2);
    const vatAmount = invoice.tax_amount.toFixed(2);
    
    // Generate ZATCA TLV QR - the QR should contain the Base64 encoded TLV data
    const zatcaQRBase64 = generateZATCAQR(sellerNameArabic, vatNumber, timestamp, totalWithVAT, vatAmount);
    
    // QR Code
    try {
      const qrCodeDataUrl = await QRCode.toDataURL(zatcaQRBase64, { 
        width: 300, 
        margin: 1,
        errorCorrectionLevel: 'M'
      });
      const qrY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : 220;
      doc.addImage(qrCodeDataUrl, 'PNG', 155, qrY, 42, 42);
      doc.setFontSize(7);
      doc.setTextColor(100);
      doc.text('ZATCA E-Invoice QR', 176, qrY + 45, { align: 'center' });
    } catch (err) {
      console.error('QR Code generation failed:', err);
    }
    
    // Download PDF directly
    doc.save(`Invoice-${invoice.invoice_number}.pdf`);
    toast.success('PDF downloaded');
  };

  const resetForm = () => {
    setFormData({
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: '',
      customer_id: '',
      project_id: '',
      client_name_arabic: '',
      purchase_order_no: '',
      subject: '',
      items: [{ description: '', quantity: 1, unit: 'unit', unit_price: 0, total: 0, inventory_id: '' }],
      subtotal: 0,
      vat_rate: 15,
      vat_amount: 0,
      total: 0,
      amount_paid: 0,
      amount_remaining: 0,
      payment_terms: '',
      notes: ''
    });
    setSelectedCustomer(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'planning',
      sent: 'in_progress',
      paid: 'completed',
      overdue: 'overdue',
      cancelled: 'on_hold'
    };
    return colors[status] || 'planning';
  };

  const activeInvoices = invoices.filter(inv => inv.status !== 'cancelled' && !inv.is_cancelled);
  const cancelledCount = invoices.length - activeInvoices.length;
  const totalInvoices = activeInvoices.reduce((sum, inv) => sum + inv.total, 0);
  const paidInvoices = activeInvoices.filter(inv => inv.status === 'paid');
  const totalPaid = paidInvoices.reduce((sum, inv) => sum + inv.total, 0);
  const pendingInvoices = activeInvoices.filter(inv => ['draft', 'sent'].includes(inv.status));
  const totalPending = pendingInvoices.reduce((sum, inv) => sum + inv.total, 0);

  if (loading) {
    return <div className="text-center py-12">Loading invoices...</div>;
  }

  return (
    <div data-testid="invoices-page">
      <div className="page-header">
        <h2 className="page-title">Invoices</h2>
        <button
          data-testid="add-invoice-button"
          onClick={() => setShowModal(true)}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 inline mr-2" />
          New Invoice
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="stat-card">
          <div className="stat-icon blue">
            <FileText className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Total Invoices</h3>
          <p className="text-3xl font-bold text-gray-900" data-testid="stat-total-invoices">{activeInvoices.length}</p>
          {cancelledCount > 0 && (
            <p className="text-xs text-red-500 mt-1" data-testid="stat-cancelled-count">+ {cancelledCount} ملغاة (غير محتسبة)</p>
          )}
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <FileText className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Paid</h3>
          <p className="text-3xl font-bold text-green-600">{totalPaid.toLocaleString()} SAR</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon yellow">
            <FileText className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Pending</h3>
          <p className="text-3xl font-bold text-yellow-600">{totalPending.toLocaleString()} SAR</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">
            <FileText className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Total Value</h3>
          <p className="text-3xl font-bold text-purple-600">{totalInvoices.toLocaleString()} SAR</p>
        </div>
      </div>

      {/* ZATCA Alerts Section */}
      {zatcaAlerts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-amber-800">
              تنبيه ZATCA: {zatcaAlerts.length} فاتورة لم يتم إرسالها
            </h3>
          </div>
          <div className="space-y-2">
            {zatcaAlerts.slice(0, 5).map(inv => {
              const daysDiff = Math.floor((new Date() - new Date(inv.created_at)) / (1000 * 60 * 60 * 24));
              return (
                <div key={inv.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-100">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-4 h-4 text-amber-600" />
                    <div>
                      <span className="font-medium text-gray-800">{inv.invoice_number}</span>
                      <span className="text-gray-500 text-sm mx-2">•</span>
                      <span className="text-gray-600 text-sm">{inv.client_name}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-amber-700 text-sm">منذ {daysDiff} يوم</span>
                    <button
                      onClick={() => submitToZATCA(inv.id)}
                      disabled={submittingToZATCA}
                      className="btn-primary text-xs py-1 px-3 bg-green-600 hover:bg-green-700 flex items-center gap-1"
                    >
                      <Send className="w-3 h-3" />
                      إرسال
                    </button>
                  </div>
                </div>
              );
            })}
            {zatcaAlerts.length > 5 && (
              <p className="text-amber-700 text-sm text-center">
                و {zatcaAlerts.length - 5} فاتورة أخرى...
              </p>
            )}
          </div>
        </div>
      )}

      {/* Main Content - Split View */}
      <div className="flex gap-4">
        {/* Invoices Table */}
        <div className={`table-container transition-all duration-300 ${selectedInvoice ? 'w-1/2' : 'w-full'}`}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Invoice No</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Total</th>
                <th>Status</th>
                <th>ZATCA</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length > 0 ? (
                invoices.map((invoice) => (
                  <tr 
                    key={invoice.id} 
                    ref={registerRow(invoice.id)}
                    data-testid={`invoice-row-${invoice.id}`}
                    onClick={() => setSelectedInvoice(selectedInvoice?.id === invoice.id ? null : invoice)}
                    className={`cursor-pointer hover:bg-blue-50 transition-colors ${selectedInvoice?.id === invoice.id ? 'bg-blue-100 border-l-4 border-blue-500' : ''} ${focusedId === invoice.id ? 'focus-highlight' : ''}`}
                  >
                    <td className="font-semibold text-blue-600">{invoice.invoice_number}</td>
                    <td>{new Date(invoice.created_at).toLocaleDateString()}</td>
                    <td className="font-medium">{invoice.client_name}</td>
                    <td className="font-bold">{invoice.total.toLocaleString()} SAR</td>
                    <td>
                      <span className={`status-badge ${getStatusColor(invoice.status)}`}>
                        {invoice.status === 'cancelled' ? 'ملغاة' : invoice.status}
                      </span>
                    </td>
                    <td>
                      {getZATCAStatusBadge(invoice.zatca_status)}
                    </td>
                    <td>
                      <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => downloadPDF(invoice)}
                          className="btn-icon"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4 text-blue-600" />
                        </button>
                        {canCreditNote(invoice) && (
                          <button
                            data-testid={`credit-note-invoice-${invoice.id}`}
                            onClick={() => openCreditNoteModal(invoice)}
                            className="btn-icon"
                            title="Create Credit Note"
                          >
                            <RotateCcw className="w-4 h-4 text-amber-600" />
                          </button>
                        )}
                        <button
                          data-testid={`lifecycle-invoice-${invoice.id}`}
                          onClick={() => setLifecycleInvoiceId(invoice.id)}
                          className="btn-icon"
                          title="عرض دورة الحياة (Lifecycle)"
                        >
                          <Activity className="w-4 h-4 text-sky-600" />
                        </button>
                        {invoice.status === 'draft' && (
                          <ActionGatedButton
                            action="invoice.post"
                            ctx={invoice}
                            data-testid={`post-invoice-${invoice.id}`}
                            onClick={() => handleStatusChange(invoice.id, 'sent')}
                            className="btn-icon"
                            title="ترحيل الفاتورة (Post)"
                          >
                            <Send className="w-4 h-4 text-emerald-600" />
                          </ActionGatedButton>
                        )}
                        {canDelete(invoice) && (
                          <button
                            data-testid={`delete-invoice-${invoice.id}`}
                            onClick={() => handleDelete(invoice)}
                            className="btn-icon delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-500">
                    No invoices found. Create your first invoice!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Invoice Details Panel */}
        {selectedInvoice && (
          <div className="w-1/2 bg-white rounded-xl shadow-lg border border-gray-200 p-6 sticky top-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selectedInvoice.invoice_number}</h3>
                <p className="text-sm text-gray-500">Invoice Details</p>
              </div>
              <button 
                onClick={() => setSelectedInvoice(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* ZATCA Status */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-gray-700">حالة ZATCA:</span>
                {getZATCAStatusBadge(selectedInvoice.zatca_status)}
              </div>
              {(!selectedInvoice.zatca_status || 
                !['reported', 'cleared'].includes((selectedInvoice.zatca_status || '').toLowerCase())) && (
                <button
                  onClick={() => submitToZATCA(selectedInvoice.id)}
                  disabled={submittingToZATCA}
                  className="btn-primary text-sm py-1 px-3 bg-green-600 hover:bg-green-700 flex items-center gap-1"
                  data-testid="submit-to-zatca-btn"
                >
                  {submittingToZATCA ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      جاري الإرسال...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      إرسال إلى ZATCA
                    </>
                  )}
                </button>
              )}
            </div>

            {/* ZATCA Error Detail — Iter-146: surface the actual rejection
                reason so users can act on it instead of staring at a silent
                red badge. Shown only when status is error / rejected. */}
            {['error', 'rejected'].includes((selectedInvoice.zatca_status || '').toLowerCase()) && (
              <div
                data-testid="zatca-error-detail"
                className="border border-red-200 bg-red-50 rounded-lg p-3 mb-4"
              >
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                  <div className="text-xs font-bold text-red-700">
                    سبب رفض ZATCA / ZATCA rejection reason
                  </div>
                </div>
                {selectedInvoice.zatca_error ? (
                  <pre
                    className="text-[11px] text-red-900 whitespace-pre-wrap break-words bg-white/60 rounded p-2 max-h-48 overflow-auto font-mono direction-ltr"
                    dir="ltr"
                  >
                    {(() => {
                      try {
                        const parsed = typeof selectedInvoice.zatca_error === "string"
                          ? JSON.parse(selectedInvoice.zatca_error)
                          : selectedInvoice.zatca_error;
                        return JSON.stringify(parsed, null, 2);
                      } catch {
                        return String(selectedInvoice.zatca_error);
                      }
                    })()}
                  </pre>
                ) : (
                  <p className="text-[11px] text-red-900">
                    لم تُحفظ تفاصيل الخطأ. أعد المحاولة عبر زر &quot;إرسال إلى ZATCA&quot;
                    أعلاه — وستظهر الرسالة المُفصَّلة هنا.
                  </p>
                )}
              </div>
            )}

            {/* B2C-misclassification warning — Iter-146: if the customer has
                a valid 15-digit VAT but the invoice is still simplified-B2C,
                ZATCA will reject it for value > 1000 SAR. */}
            {(() => {
              const cv = (selectedInvoice.customer_vat_number || '').trim();
              const isSimplified = (selectedInvoice.invoice_type || '').toLowerCase().includes('simpl')
                || (selectedInvoice.zatca_invoice_type || '') === '0200000';
              const totalGT1000 = (parseFloat(selectedInvoice.total_with_vat || selectedInvoice.total || 0) || 0) > 1000;
              if (cv && cv.length === 15 && isSimplified && totalGT1000) {
                return (
                  <div
                    data-testid="zatca-b2b-mismatch-warning"
                    className="border border-amber-300 bg-amber-50 rounded-lg p-3 mb-4 text-[11px] text-amber-900"
                  >
                    <div className="font-bold mb-1">⚠️ تحذير — نوع الفاتورة قد لا يتطابق مع ZATCA</div>
                    العميل لديه رقم ضريبي صالح ({cv}) والمبلغ يتجاوز 1000 ر.س،
                    لذا يجب أن تكون <b>فاتورة معيارية B2B (Standard)</b> وليس مبسطة.
                    قم بتعديل تصنيف الفاتورة قبل الإرسال أو تأكد من أن إعداد
                    العميل لديك يُسجّل الرقم الضريبي في حقل <code>vat_number</code>.
                  </div>
                );
              }
              return null;
            })()}

            {/* Project Assignment — fixes Project Profit ↔ GL reconciliation
                FAIL on legacy invoices that were created before the Project
                dropdown existed. The PATCH endpoint accepts ``project_id``
                (Invoice model carries it). */}
            <div
              data-testid="invoice-project-assignment-card"
              className={`mb-4 rounded-lg p-3 text-sm border ${
                selectedInvoice.project_id
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-amber-50 border-amber-200'
              }`}
            >
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="font-semibold text-gray-800">
                  المشروع / Project
                </span>
                {selectedInvoice.project_id ? (
                  <span className="text-xs text-emerald-700 font-medium">
                    ✓ مرتبط بمشروع
                  </span>
                ) : (
                  <span className="text-xs text-amber-700 font-medium">
                    ⚠ غير مرتبط — يسبب FAIL في Project Profit ↔ GL
                  </span>
                )}
              </div>
              <select
                data-testid="invoice-detail-project-select"
                value={selectedInvoice.project_id || ''}
                onChange={async (e) => {
                  const newProjectId = e.target.value || null;
                  try {
                    const res = await axiosInstance.put(
                      `/invoices/${selectedInvoice.id}`,
                      { project_id: newProjectId },
                    );
                    setSelectedInvoice({ ...selectedInvoice, project_id: newProjectId });
                    fetchData();
                    const synced = res?.data?.journal_sync?.updated || 0;
                    if (newProjectId) {
                      toast.success(
                        synced > 0
                          ? `تم ربط الفاتورة وتحديث ${synced} قيد محاسبي — سيُحَل Project Profit ↔ GL فوراً`
                          : 'تم ربط الفاتورة (لا يوجد قيد مرتبط بعد)'
                      );
                    } else {
                      toast.success('تم إلغاء ربط المشروع');
                    }
                  } catch (err) {
                    toast.error(err?.response?.data?.detail
                      || 'فشل تحديث المشروع — تحقق من صلاحياتك');
                  }
                }}
                className="form-select w-full text-sm"
              >
                <option value="">— Unassigned —</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.code ? `${p.code} · ` : ''}{p.name || p.project_name || p.id}
                  </option>
                ))}
              </select>
              {!selectedInvoice.project_id && (
                <p className="text-xs text-amber-700 mt-2 leading-relaxed">
                  هذه الفاتورة (إجمالي {Number(selectedInvoice.total || 0).toLocaleString()} SAR)
                  لم تُربط بأي مشروع. هذا هو سبب
                  <strong> Project Profit ↔ GL FAIL</strong> في Dashboard.
                  اختر المشروع الصحيح من القائمة ليُحَل تلقائياً.
                </p>
              )}
            </div>

            {/* Contract Integrity — Revision Lineage Card.
                Surfaces the tamper-evident proof of WHICH quotation
                revision content was invoiced. The hash is the seal:
                recomputing it on the source quotation MUST match. */}
            {selectedInvoice.source_quotation_id && (
              <div
                data-testid="invoice-contract-lineage-card"
                className="mb-4 border border-violet-200 bg-violet-50 rounded-lg p-3 text-sm"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 text-violet-900 font-semibold">
                    <Building2 className="w-4 h-4" />
                    Contract Lineage
                  </div>
                  {selectedInvoice.source_revision_hash && (
                    <span
                      data-testid="invoice-revision-hash-chip"
                      title={`Source revision content fingerprint (SHA-256): ${selectedInvoice.source_revision_hash}`}
                      className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-white border border-violet-200 text-violet-800 font-mono"
                    >
                      {String(selectedInvoice.source_revision_hash).slice(0, 12)}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-violet-900">
                  <div>
                    <div className="opacity-60">Source quotation</div>
                    <div className="font-mono" data-testid="invoice-source-quotation-no">
                      {selectedInvoice.source_quotation_number || '—'}
                    </div>
                  </div>
                  <div>
                    <div className="opacity-60">Revision invoiced</div>
                    <div className="font-mono" data-testid="invoice-source-revision-number">
                      R{selectedInvoice.source_revision_number ?? 0}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Status & Actions */}
            <div className="flex items-center gap-3 mb-6">
              <select
                value={selectedInvoice.status}
                onChange={(e) => {
                  handleStatusChange(selectedInvoice.id, e.target.value);
                  setSelectedInvoice({...selectedInvoice, status: e.target.value});
                }}
                className={`status-badge ${getStatusColor(selectedInvoice.status)} cursor-pointer border-0 text-sm`}
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <button
                onClick={() => downloadPDF(selectedInvoice)}
                className="btn-primary text-sm py-1 px-3"
              >
                <Download className="w-4 h-4 inline mr-1" />
                PDF
              </button>
            </div>

            {/* Customer Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">Customer Information</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">Name:</span>
                  <p className="font-medium">{selectedInvoice.client_name}</p>
                  {selectedInvoice.client_name_arabic && (
                    <p className="text-gray-600">{selectedInvoice.client_name_arabic}</p>
                  )}
                </div>
                <div>
                  <span className="text-gray-500">Address:</span>
                  <p className="font-medium">{selectedInvoice.customer_address || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">CR Number:</span>
                  <p className="font-medium">{selectedInvoice.customer_cr_number || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">VAT Number:</span>
                  <p className="font-medium">{selectedInvoice.customer_vat_number || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Invoice Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">Invoice Information</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">Invoice Date:</span>
                  <p className="font-medium">{new Date(selectedInvoice.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-gray-500">Due Date:</span>
                  <p className="font-medium">{new Date(selectedInvoice.due_date).toLocaleDateString()}</p>
                </div>
                {selectedInvoice.purchase_order_no && (
                  <div>
                    <span className="text-gray-500">P.O. Number:</span>
                    <p className="font-medium">{selectedInvoice.purchase_order_no}</p>
                  </div>
                )}
                {selectedInvoice.subject && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Subject:</span>
                    <p className="font-medium">{selectedInvoice.subject}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Items */}
            <div className="mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">Items</h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-center">Qty</th>
                      <th className="px-3 py-2 text-right">Price</th>
                      <th className="px-3 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.items?.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2">{item.description}</td>
                        <td className="px-3 py-2 text-center">{item.quantity} {item.unit}</td>
                        <td className="px-3 py-2 text-right">{item.unit_price?.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-medium">{item.total?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex justify-between text-sm mb-1">
                <span>Subtotal:</span>
                <span>{selectedInvoice.subtotal?.toLocaleString()} SAR</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span>VAT ({selectedInvoice.tax_rate || 15}%):</span>
                <span>{selectedInvoice.tax_amount?.toLocaleString()} SAR</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                <span>Total:</span>
                <span className="text-blue-600">{selectedInvoice.total?.toLocaleString()} SAR</span>
              </div>
              {selectedInvoice.amount_paid > 0 && (
                <>
                  <div className="flex justify-between text-sm text-green-600 mt-2">
                    <span>Amount Paid:</span>
                    <span>{selectedInvoice.amount_paid?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Amount Remaining:</span>
                    <span>{selectedInvoice.amount_remaining?.toLocaleString()} SAR</span>
                  </div>
                </>
              )}
            </div>

            {/* Notes */}
            {selectedInvoice.notes && (
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Notes</h4>
                <p className="text-sm text-gray-600">{selectedInvoice.notes}</p>
              </div>
            )}

            {/* Created By */}
            <div className="mt-4 text-xs text-gray-400">
              Created by: {selectedInvoice.user_name || 'N/A'}
            </div>

            {/* P7 · Invoice Amendment Versioning */}
            <InvoiceVersionsPanel
              invoice={selectedInvoice}
              onInvoiceUpdated={(updated) => {
                setSelectedInvoice(updated);
                fetchData();
              }}
            />
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">New Invoice</h3>
              <button
                data-testid="close-modal-button"
                onClick={() => setShowModal(false)}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="form-group">
                  <label className="form-label">Invoice Date *</label>
                  <input
                    data-testid="invoice-date-input"
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Due Date *</label>
                  <input
                    data-testid="due-date-input"
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Purchase Order No</label>
                  <input
                    data-testid="po-number-input"
                    type="text"
                    value={formData.purchase_order_no}
                    onChange={(e) => setFormData({ ...formData, purchase_order_no: e.target.value })}
                    className="form-input"
                    placeholder="P.O. Number"
                  />
                </div>
              </div>

              {/* Customer Selection */}
              <div className="form-group mb-4">
                <label className="form-label">Customer *</label>
                <select
                  data-testid="customer-select"
                  value={formData.customer_id}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Select Customer</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name_english || customer.name_arabic}
                    </option>
                  ))}
                </select>
              </div>

              {/* Project Selection — required for Project Profit ↔ GL reconciliation */}
              <div className="form-group mb-4">
                <label className="form-label">
                  Project / المشروع
                  <span className="text-xs text-amber-700 ml-2 font-normal">
                    (مطلوب لتسوية Project Profit ↔ GL)
                  </span>
                </label>
                <select
                  data-testid="invoice-project-select"
                  value={formData.project_id}
                  onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                  className="form-select"
                >
                  <option value="">— Unassigned (will trigger reconciliation FAIL) —</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.code ? `${p.code} · ` : ''}{p.name || p.project_name || p.id}
                    </option>
                  ))}
                </select>
                {!formData.project_id && (
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ ترك المشروع فارغاً يعني أن إيراد هذه الفاتورة لن يُربط بأي مشروع، وستظهر فجوة في تقرير Project Profit ↔ GL.
                  </p>
                )}
              </div>

              {/* Customer Details (Auto-filled) */}
              {selectedCustomer && (
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Customer Details</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Name (Arabic):</span>
                      <input
                        type="text"
                        value={formData.client_name_arabic}
                        onChange={(e) => setFormData({ ...formData, client_name_arabic: e.target.value })}
                        className="form-input text-sm mt-1"
                        placeholder="اسم العميل بالعربي"
                        dir="rtl"
                      />
                    </div>
                    <div>
                      <span className="text-gray-600">Address:</span>
                      <p className="font-medium">{selectedCustomer.address || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">CR Number:</span>
                      <p className="font-medium">{selectedCustomer.cr_number || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">VAT Number:</span>
                      <p className="font-medium">{selectedCustomer.vat_number || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Subject */}
              <div className="form-group mb-4">
                <label className="form-label">Subject</label>
                <input
                  data-testid="subject-input"
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="form-input"
                  placeholder="Invoice subject (optional)"
                />
              </div>

              {/* Items */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-3">
                  <label className="form-label mb-0">Items *</label>
                  <button
                    type="button"
                    onClick={addItem}
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-1"
                  >
                    <PlusCircle className="w-4 h-4" />
                    Add Item
                  </button>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-center w-16">S.No</th>
                        <th className="px-3 py-2 text-left">Description *</th>
                        <th className="px-3 py-2 text-center w-20">Qty</th>
                        <th className="px-3 py-2 text-center w-24">Unit</th>
                        <th className="px-3 py-2 text-right w-28">Unit Price</th>
                        <th className="px-3 py-2 text-right w-28">Total</th>
                        <th className="px-3 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.items.map((item, index) => (
                        <tr key={index} className="border-t">
                          <td className="px-3 py-2 text-center font-medium text-gray-600">
                            {index + 1}
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={item.inventory_id || ''}
                              onChange={(e) => handleItemChange(index, 'inventory_id', e.target.value)}
                              className="form-select text-sm"
                              required
                            >
                              <option value="">Select Item</option>
                              {inventory.map(inv => (
                                <option key={inv.id} value={inv.id}>
                                  {inv.name || inv.item_name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                              className="form-input text-sm text-center"
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={item.unit}
                              onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                              className="form-select text-sm"
                            >
                              <option value="unit">Unit</option>
                              <option value="pcs">Pcs</option>
                              <option value="m">m</option>
                              <option value="m2">m²</option>
                              <option value="m3">m³</option>
                              <option value="kg">kg</option>
                              <option value="ton">Ton</option>
                              <option value="hr">Hour</option>
                              <option value="day">Day</option>
                              <option value="lot">Lot</option>
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              value={item.unit_price}
                              onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                              className="form-input text-sm text-right"
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {item.total.toFixed(2)} SAR
                          </td>
                          <td className="px-3 py-2">
                            {formData.items.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeItem(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="mt-4 flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">{formData.subtotal.toFixed(2)} SAR</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">VAT:</span>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={formData.vat_rate}
                          onChange={(e) => handleVatChange(parseFloat(e.target.value) || 0)}
                          className="form-input w-16 text-sm text-center"
                          min="0"
                          max="100"
                        />
                        <span>%</span>
                        <span className="font-medium">{formData.vat_amount.toFixed(2)} SAR</span>
                      </div>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Total:</span>
                      <span className="text-blue-600">{formData.total.toFixed(2)} SAR</span>
                    </div>
                    <div className="flex justify-between items-center border-t pt-2">
                      <span className="text-gray-600">Amount Paid:</span>
                      <input
                        type="number"
                        value={formData.amount_paid}
                        onChange={(e) => handleAmountPaidChange(e.target.value)}
                        className="form-input w-32 text-sm text-right"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="flex justify-between text-lg font-bold text-red-600">
                      <span>Remaining:</span>
                      <span>{formData.amount_remaining.toFixed(2)} SAR</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Terms & Notes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="form-group">
                  <label className="form-label">Payment Terms</label>
                  <input
                    type="text"
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    className="form-input"
                    placeholder="e.g., Net 30, Due upon receipt"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="form-input"
                    rows="2"
                    placeholder="Additional notes..."
                  />
                </div>
              </div>

              {/* Bank Details */}
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h4 className="font-semibold text-blue-800 mb-3">Bank Details</h4>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 block">Bank Name</span>
                    <span className="font-medium">ALAHLI BANK (SNB)</span>
                  </div>
                  <div>
                    <span className="text-gray-600 block">Account</span>
                    <span className="font-medium">24700000524008</span>
                  </div>
                  <div>
                    <span className="text-gray-600 block">IBAN</span>
                    <span className="font-medium">SA6910000024700000524008</span>
                  </div>
                  <div>
                    <span className="text-gray-600 block">SWIFT</span>
                    <span className="font-medium">NCBKSAJE</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  data-testid="submit-invoice-button"
                  type="submit"
                  className="btn-primary flex-1"
                >
                  Create Invoice
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Credit Note Modal (Phase Accounting-CN-1) ────────── */}
      {showCNModal && cnInvoice && (
        <div className="modal-overlay" onClick={() => !cnBusy && setShowCNModal(false)} data-testid="credit-note-modal">
          <div className="modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="flex items-center gap-2"><RotateCcw className="w-5 h-5 text-amber-600" /> Credit Note for {cnInvoice.invoice_number}</h2>
              <button onClick={() => setShowCNModal(false)} className="btn-icon" disabled={cnBusy}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-slate-500">Invoice Total:</span> <b>{(cnInvoice.total||0).toLocaleString()} SAR</b></div>
                  <div><span className="text-slate-500">Tax Rate:</span> <b>{cnInvoice.tax_rate || 15}%</b></div>
                  <div><span className="text-slate-500">Already Credited:</span> <b className="text-amber-700">{(cnInvoice.credited_total||0).toLocaleString()} SAR</b></div>
                  <div><span className="text-slate-500">Refund State:</span> <b>{cnInvoice.refund_state || 'none'}</b></div>
                </div>
              </div>

              {cnExisting.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500 mb-2">Existing Credit Notes ({cnExisting.length})</div>
                  <div className="border border-slate-200 rounded divide-y max-h-32 overflow-y-auto">
                    {cnExisting.map(c => (
                      <div key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                        <span className="font-mono text-xs">{c.credit_note_number}</span>
                        <span className="text-slate-500">{c.type}</span>
                        <span className="ml-auto font-mono">{c.total_amount.toLocaleString()}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${c.status==='posted'?'bg-green-100 text-green-700':c.status==='reversed'?'bg-slate-200 text-slate-600':'bg-amber-100 text-amber-700'}`}>{c.status}</span>
                        {c.status === 'draft' && (
                          <button onClick={() => postExistingCN(c.id)} className="text-xs text-blue-600 hover:underline" data-testid={`post-cn-${c.id}`}>Post</button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t pt-4 space-y-3">
                <div className="text-xs font-semibold uppercase text-slate-500">Create New Credit Note</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-600">Amount (excl. tax) *</label>
                    <input type="number" min="0.01" step="0.01" data-testid="cn-amount-input"
                      value={cnForm.amount}
                      onChange={(ev) => setCnForm({...cnForm, amount: ev.target.value})}
                      className="input-field w-full" placeholder="0.00" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600">Type *</label>
                    <select data-testid="cn-type-select"
                      value={cnForm.type}
                      onChange={(ev) => setCnForm({...cnForm, type: ev.target.value})}
                      className="input-field w-full">
                      <option value="partial_refund">Partial Refund</option>
                      <option value="full_refund">Full Refund</option>
                      <option value="correction">Correction</option>
                      <option value="discount_adjustment">Discount Adjustment</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-600">Reason * (min 3 chars)</label>
                  <input type="text" data-testid="cn-reason-input"
                    value={cnForm.reason}
                    onChange={(ev) => setCnForm({...cnForm, reason: ev.target.value})}
                    className="input-field w-full" placeholder="e.g. Service not delivered" />
                </div>
                <div>
                  <label className="text-xs text-slate-600">Notes (optional)</label>
                  <textarea data-testid="cn-notes-input"
                    value={cnForm.notes}
                    onChange={(ev) => setCnForm({...cnForm, notes: ev.target.value})}
                    rows={2} className="input-field w-full" />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t">
                <button onClick={() => setShowCNModal(false)} className="btn-secondary" disabled={cnBusy}>Cancel</button>
                <button onClick={() => submitCreditNote(false)} disabled={cnBusy} data-testid="cn-save-draft"
                  className="px-4 py-2 rounded border border-slate-300 hover:bg-slate-50">Save as Draft</button>
                <button onClick={() => submitCreditNote(true)} disabled={cnBusy} data-testid="cn-create-and-post"
                  className="btn-primary">{cnBusy ? 'Working…' : 'Create & Post to Accounting'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lifecycle Wizard — Phase 5 */}
      {lifecycleInvoiceId && (
        <InvoiceLifecycleWizard
          invoiceId={lifecycleInvoiceId}
          onClose={() => setLifecycleInvoiceId(null)}
        />
      )}
    </div>
  );
};

export default Invoices;
